import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Message component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconMessage extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon cat-c-icon-bi-directional', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.97605 2.60498C4.76102 2.60498 4.55479 2.6904 4.40275 2.84245C4.2507 2.9945 4.16528 3.20072 4.16528 3.41575V17.4627C4.16528 17.5433 4.15228 17.6234 4.12679 17.6998L2.99807 21.086L7.91568 19.8566C7.97517 19.8417 8.03626 19.8342 8.09758 19.8342H20.5837C20.7988 19.8342 21.005 19.7488 21.157 19.5967C21.3091 19.4447 21.3945 19.2385 21.3945 19.0234V3.41575C21.3945 3.20072 21.3091 2.9945 21.157 2.84245C21.005 2.6904 20.7988 2.60498 20.5837 2.60498H4.97605ZM3.34209 1.78179C3.77544 1.34844 4.36319 1.10498 4.97605 1.10498H20.5837C21.1966 1.10498 21.7843 1.34844 22.2177 1.78179C22.6511 2.21514 22.8945 2.8029 22.8945 3.41575V19.0234C22.8945 19.6363 22.6511 20.2241 22.2177 20.6574C21.7843 21.0908 21.1966 21.3342 20.5837 21.3342H8.18991L2.03641 22.8726C1.77085 22.939 1.49021 22.8554 1.30422 22.6546C1.11823 22.4537 1.05643 22.1675 1.143 21.9078L2.66528 17.341V3.41575C2.66528 2.8029 2.90873 2.21514 3.34209 1.78179Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-message') === undefined) {
  customElements.define('cat-icon-message', BlocksIconMessage);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-message': BlocksIconMessage;
  }
}
